/**
 * Hook for looking up a single pipeline stage by entity ID.
 *
 * Reads directly from the CRM object store and triggers a fetch if the stage
 * is not yet cached. This is the preferred hook when a component needs exactly
 * one stage (e.g., a proposal card resolving a target_entity_id association).
 *
 * Prefer this over useStages when:
 * - You only need one specific stage by entity_id
 * - You don't need to enumerate stages for an object type
 *
 * Use useStages instead when:
 * - You need getStagesForObjectType / getStagesByType (cross-pipeline scans)
 * - You need the full sorted list of stages for a pipeline
 */

import { useMemo, useSyncExternalStore } from 'react';

import { useCrmObjectSnapshot } from '@/lib/crm/hooks/useCrmObjectSnapshot';
import {
  getFailureVersion,
  hasFailedFetch,
  subscribeToFetchFailures,
} from '@/lib/crm/object-cards/entityFetchBatcher';

import type { Stage } from './useStages';
import { transformToStage } from './useStages';

/**
 * Look up a single pipeline stage from the CRM object store.
 *
 * @param orgId - Organisation ID; when undefined no fetch is triggered.
 * @param stageEntityId - The entity_id of the stage CrmObject to resolve.
 * @returns `{ stage, isLoading, hasFailed }` — stage is undefined while
 *   loading or when stageEntityId is undefined; hasFailed flips true when
 *   the entity batcher records a fetch failure for the requested id, so
 *   callers can render a safe generic fallback instead of a raw UUID.
 *
 * @example
 * const { stage, isLoading } = useStageById(orgId, targetEntityId);
 * if (!isLoading && stage) {
 *   return <span>{stage.label}</span>;
 * }
 */
export function useStageById(
  orgId: number | undefined,
  stageEntityId: string | undefined
): { stage: Stage | undefined; isLoading: boolean; hasFailed: boolean } {
  const obj = useCrmObjectSnapshot(stageEntityId, orgId);

  // Re-render whenever the entity-fetch failure set changes so a stage that
  // failed to resolve exits loading instead of spinning forever. The batcher
  // marks an id as failed when its fetch errored or the server didn't return
  // it; subscribing here lets isLoading flip false on that signal.
  useSyncExternalStore(subscribeToFetchFailures, getFailureVersion);

  const stage = useMemo(() => (obj ? transformToStage(obj) : undefined), [obj]);

  // Mirror useCrmObjectSnapshot's fetch guard: it only kicks off a fetch when
  // orgId > 0, so non-positive orgIds must not show as loading (would never
  // resolve). Also exit loading once the batcher has recorded a failure for
  // this id — without that, a permanent fetch failure would keep the UI in
  // the loading state indefinitely.
  const hasFailed = Boolean(stageEntityId && hasFailedFetch(stageEntityId));
  const isLoading = Boolean(
    orgId && orgId > 0 && stageEntityId && !obj && !hasFailed
  );

  return { stage, isLoading, hasFailed };
}
